import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Wrench, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import PageHero from "@/components/PageHero";
import { Container, ShoreBadge, EmptyState } from "@/components/shared";

const SHORES = [
  { key: "all", label: "Both shores" },
  { key: "lincolnshire", label: "Lincolnshire" },
  { key: "massachusetts", label: "Massachusetts" },
];

export default function Trade() {
  const [products, setProducts] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [shore, setShore] = useState("all");
  const [sector, setSector] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/sectors").then((r) => setSectors(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: { shore, sector } }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [shore, sector]);

  const grouped = useMemo(() => {
    const map = {};
    products.forEach((p) => {
      (map[p.sector] = map[p.sector] || []).push(p);
    });
    return map;
  }, [products]);

  return (
    <div>
      <PageHero
        kicker="The trade floor · This is trade"
        title="Products & services across the crossing"
        intro="A working B2B marketplace of goods and services offered by firms on both shores. Company profiles in the directory are free; published products and services here are the mark of a paying member trading in earnest."
      />

      <section className="py-12 sm:py-16">
        <Container>
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center justify-between mb-10 pb-6 border-b border-[#D6D3CB]">
            <div className="flex flex-wrap gap-2" data-testid="trade-shore-filters">
              {SHORES.map((s) => (
                <button key={s.key} data-testid={`trade-shore-${s.key}`} onClick={() => setShore(s.key)}
                  className={`font-sans text-sm px-4 py-2 border transition-colors ${shore === s.key ? "bg-[#1E1E2F] text-[#FFFAF3] border-[#1E1E2F]" : "bg-[#FFFAF3] text-[#1B2430] border-[#D6D3CB] hover:border-[#CDA434]"}`}>
                  {s.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <label className="font-sans text-xs uppercase tracking-wider text-[#505A68]">Sector</label>
              <select data-testid="trade-sector" value={sector} onChange={(e) => setSector(e.target.value)}
                className="bg-[#FFFAF3] border border-[#D6D3CB] focus:border-[#003366] px-4 py-2 text-sm text-[#1B2430] font-sans">
                <option value="all">All sectors</option>
                {sectors.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => <div key={i} className="h-72 bg-[#FFFAF3] border border-[#D6D3CB] animate-pulse" />)}
            </div>
          ) : products.length === 0 ? (
            <EmptyState testId="trade-empty" icon={<Package size={22} />} heading="No goods on this heading yet" text="No products or services match this shore and sector. Try a wider filter, or become a member to list your own." />
          ) : (
            <div className="space-y-14">
              {Object.keys(grouped).map((sec) => (
                <div key={sec}>
                  <div className="flex items-center gap-3 mb-6">
                    <h2 className="font-serif text-xl text-[#003366]">{sec}</h2>
                    <div className="flex-1 h-px bg-[#D6D3CB]" />
                    <span className="font-sans text-xs text-[#505A68]">{grouped[sec].length} listing{grouped[sec].length === 1 ? "" : "s"}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {grouped[sec].map((p) => <ProductCard key={p.id} p={p} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Container>
      </section>
    </div>
  );
}

export function ProductCard({ p }) {
  return (
    <div data-testid={`product-card-${p.id}`} className="group flex flex-col bg-[#FFFAF3] border border-[#D6D3CB] hover:border-[#CDA434] transition-colors overflow-hidden">
      {p.image_url && (
        <div className="relative aspect-[4/3] overflow-hidden bg-[#F8F1E7]">
          <img src={p.image_url} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <span className="absolute top-3 left-3"><ShoreBadge shore={p.shore} /></span>
        </div>
      )}
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2">
          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 border ${p.type === "service" ? "bg-[#003366]/10 text-[#003366] border-[#003366]/30" : "bg-[#CDA434]/15 text-[#7a611c] border-[#CDA434]/50"}`}>
            {p.type === "service" ? <Wrench size={11} /> : <Package size={11} />} {p.type}
          </span>
        </div>
        <h3 className="font-serif text-lg text-[#1E1E2F] leading-snug">{p.name}</h3>
        {p.price && <p className="font-sans text-sm font-semibold text-[#7a611c] mt-1">{p.price}</p>}
        <p className="font-sans text-sm text-[#505A68] leading-relaxed mt-3 flex-1">{p.description}</p>
        <Link to={`/made/${p.company_slug}`} className="mt-5 inline-flex items-center gap-1 font-sans text-sm font-semibold text-[#003366]">
          {p.company_name} <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </div>
    </div>
  );
}
